import type {
  BaseCabinetDescriptor,
  CabinetShape,
  CaseworkDescriptor,
  ClosetShelfDescriptor,
  FillerPanelDescriptor,
  TallCabinetDescriptor,
  UpperCabinetDescriptor,
} from "building-types/casework/caseworkDescriptor";
import {
  CabinetType,
  CaseworkShapeType,
  CaseworkSuperType,
  ShelvingType,
  UpperCabinetSubType,
} from "building-types/casework/caseworkTypesEnum";
import type { Table } from "common/data/table";
import type { CaseworkFeatures } from "doc/elements/caseworkFeatures";
import { isCaseworkV2Enabled } from "doc/elements/caseworkFeatures";

/**
 * Creation default for `shape`, gated behind Casework V2: with the flag off it stays absent, which
 * already reads as `NONE`. An explicit shape always wins, so duplicating V2 data never drops it.
 */
function cabinetShapeDefault(
  shape: CabinetShape | undefined,
  features: CaseworkFeatures | undefined,
): CabinetShape | undefined {
  if (shape !== undefined) {
    return shape;
  }

  return isCaseworkV2Enabled(features) ? { type: CaseworkShapeType.NONE } : undefined;
}

/** The box a shape is seeded against; either dimension may be unknown — see {@link boxDimensions}. */
export interface CabinetShapeBox {
  readonly width?: number;
  readonly depth?: number;
}

/** How far a seeded cut reaches down each of the two edges meeting at the corner it takes. */
const DEFAULT_CORNER_LEG = 12;

/** Stands in for a dimension the caller doesn't know, as a catalog descriptor's depth never is. */
const NOMINAL_BOX_DIMENSION = 24;

function boxDimensions({ width, depth }: CabinetShapeBox): [number, number] {
  return [width ?? NOMINAL_BOX_DIMENSION, depth ?? NOMINAL_BOX_DIMENSION];
}

/**
 * The leg every seeded corner is cut by, held to half of the shorter edge so a small box still keeps
 * a face standing either side of the cut. Equal legs are what put the seeded angled face at 45°.
 */
function seedCornerLeg(box: CabinetShapeBox): number {
  const [width, depth] = boxDimensions(box);

  return Math.min(DEFAULT_CORNER_LEG, width / 2, depth / 2);
}

/**
 * Front widths seeded the first time each corner shape is picked, both from the one corner
 * {@link seedCornerLeg} cuts — so the first cut is the same size whatever box it lands on.
 */
function seededFrontWidths(type: CaseworkShapeType, box: CabinetShapeBox): number[] {
  if (type === CaseworkShapeType.NONE) {
    return [];
  }

  const [width, depth] = boxDimensions(box);
  const leg = seedCornerLeg(box);

  // L-shaped stores the notch's two legs, which run from the corner being cut; angled stores the two
  // faces that cut leaves standing, which run back to it from the box's other corners.
  return type === CaseworkShapeType.L_SHAPED ? [leg, leg] : [width - leg, depth - leg];
}

/**
 * The shape a cabinet takes when switched to `type`. Widths and the reverse carry across position by
 * position, so a mis-click costs nothing; `NONE` carries neither, having no corner to place.
 */
export function cabinetShapeOfType(
  type: CaseworkShapeType,
  previous: CabinetShape | undefined,
  box: CabinetShapeBox = {},
): CabinetShape {
  if (type === CaseworkShapeType.NONE) {
    return { type };
  }

  const carriedShape = previous !== undefined && previous.type !== CaseworkShapeType.NONE ? previous : undefined;
  const rawCarried = carriedShape?.frontWidths ?? [];
  // L_SHAPED stores each leg's reach from the corner; ANGLED stores the face left standing beyond it
  // on the same edge, i.e. edgeLength - reach. Switching between the two shapes must flip carried
  // values across their edge, or a real (non-seeded) cut silently changes size or clamps to zero.
  const [boxWidth, boxDepth] = boxDimensions(box);
  const edgeLengths = [boxWidth, boxDepth];
  const carried =
    carriedShape !== undefined && carriedShape.type !== type
      ? rawCarried.map((value, i) => (edgeLengths[i] ?? value) - value)
      : rawCarried;
  const shape = {
    frontWidths: seededFrontWidths(type, box).map((width, i) => carried[i] ?? width),
    reverse: carriedShape?.reverse ?? false,
  };

  if (type === CaseworkShapeType.ANGLED) {
    // The back corner is left square until asked for; only another angled shape has one to carry.
    const rearWidth = carriedShape?.type === CaseworkShapeType.ANGLED ? carriedShape.rearWidth : undefined;
    return { ...shape, type, rearWidth: rearWidth ?? 0 };
  }

  return { ...shape, type };
}

export function baseCabinetDescriptorNew(
  { id, name, width = 24, shape }: Partial<BaseCabinetDescriptor> & Pick<BaseCabinetDescriptor, "id" | "name">,
  features?: CaseworkFeatures,
): BaseCabinetDescriptor {
  return {
    catalogItemType: "CASEWORK_DESCRIPTOR",
    superType: CaseworkSuperType.CABINET,
    type: CabinetType.BASE_CABINET,
    id,
    name,
    width,
    shape: cabinetShapeDefault(shape, features),
  };
}

export function upperCabinetDescriptorNew(
  {
    id,
    name,
    width = 24,
    height = 30,
    shape,
  }: Partial<UpperCabinetDescriptor> & Pick<UpperCabinetDescriptor, "id" | "name">,
  features?: CaseworkFeatures,
): UpperCabinetDescriptor {
  return {
    catalogItemType: "CASEWORK_DESCRIPTOR",
    superType: CaseworkSuperType.CABINET,
    type: CabinetType.UPPER_CABINET,
    subType: UpperCabinetSubType.GENERAL,
    id,
    name,
    width,
    height,
    shape: cabinetShapeDefault(shape, features),
  };
}

export function tallCabinetDescriptorNew(
  {
    id,
    name,
    width = 24,
    height = 84,
    shape,
  }: Partial<TallCabinetDescriptor> & Pick<TallCabinetDescriptor, "id" | "name">,
  features?: CaseworkFeatures,
): TallCabinetDescriptor {
  return {
    catalogItemType: "CASEWORK_DESCRIPTOR",
    superType: CaseworkSuperType.CABINET,
    type: CabinetType.TALL_CABINET,
    id,
    name,
    width,
    height,
    shape: cabinetShapeDefault(shape, features),
  };
}

export function fillerPanelDescriptorNew(
  { id, name, width = 3, shape }: Partial<FillerPanelDescriptor> & Pick<FillerPanelDescriptor, "id" | "name">,
  features?: CaseworkFeatures,
): FillerPanelDescriptor {
  return {
    catalogItemType: "CASEWORK_DESCRIPTOR",
    superType: CaseworkSuperType.CABINET,
    type: CabinetType.FILLER_PANEL,
    id,
    name,
    width,
    shape: cabinetShapeDefault(shape, features),
  };
}

export function closetShelfDescriptorNew({
  id,
  name,
  shelfDepth = 12,
  width = 36,
}: Partial<ClosetShelfDescriptor> & Pick<ClosetShelfDescriptor, "id" | "name">): ClosetShelfDescriptor {
  return {
    catalogItemType: "CASEWORK_DESCRIPTOR",
    superType: CaseworkSuperType.SHELVING,
    type: ShelvingType.CLOSET_SHELF,
    id,
    name,
    shelfDepth,
    width,
  };
}

const defaultBaseCabinetId = "BASE-B24";
const defaultUpperCabinetId = "UPPER-W24";
const defaultTallCabinetId = "TALL-B24";
const defaultFillerPanelId = "FILLER-3";
export const defaultCaseworkTypes: Table<CaseworkDescriptor> = {
  [defaultBaseCabinetId]: baseCabinetDescriptorNew({
    id: defaultBaseCabinetId,
    name: "default B24",
    type: CabinetType.BASE_CABINET,
    width: 24,
  }),
  [defaultUpperCabinetId]: upperCabinetDescriptorNew({
    id: defaultUpperCabinetId,
    name: "default W24",
    type: CabinetType.UPPER_CABINET,
    width: 24,
    height: 30,
  }),
  [defaultTallCabinetId]: tallCabinetDescriptorNew({
    id: defaultTallCabinetId,
    name: "default TALL-B24",
    type: CabinetType.TALL_CABINET,
    width: 24,
    height: 84,
  }),
  [defaultFillerPanelId]: fillerPanelDescriptorNew({
    id: defaultFillerPanelId,
    name: "default FILLER-3",
    type: CabinetType.FILLER_PANEL,
    width: 3,
  }),
};
